import React from 'react';
import { getProgramPrintJob, getProgramPrintResults, type ProgramPrintJobMeta } from '../../services/rpc/printJobs';

function getJobIdFromHash(): string | null {
  const hash = window.location.hash || '';
  const query = hash.split('?')[1] || '';
  const params = new URLSearchParams(query);
  return params.get('job_id');
}

const PrintResultsPage: React.FC = () => {
  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [jobMeta, setJobMeta] = React.useState<ProgramPrintJobMeta | null>(null);

  const jobId = React.useMemo(() => getJobIdFromHash(), []);

  React.useEffect(() => {
    (async () => {
      try {
        if (!jobId) {
          setError('Missing job_id in URL');
          setLoading(false);
          return;
        }
        setLoading(true);
        
        // Fetch job metadata (filters, created_at)
        const metaData = await getProgramPrintJob(jobId);
        setJobMeta(metaData);
        
        // Fetch rows
        const data = await getProgramPrintResults(jobId);
        setRows(data);
      } catch (e: unknown) {
        const msg = (e as Error)?.message || 'Failed to load print results';
        setError(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [jobId]);

  // Helper to format filters for display
  const getFilterSummary = () => {
    if (!jobMeta) return [];
    const filters = (jobMeta.filters ?? {}) as Record<string, unknown>;
    const summary: string[] = [];
    const textSearch = typeof filters.text_search === 'string' ? filters.text_search : undefined;
    const vetting = Array.isArray(filters.vetting_status) ? filters.vetting_status : undefined;
    const qualityMin = typeof filters.quality_score_min === 'number' ? filters.quality_score_min : undefined;
    const isbnRequired = typeof filters.isbn_required === 'boolean' ? filters.isbn_required : undefined;
    const indieAllowed = typeof filters.independent_publishers_allowed === 'boolean' ? filters.independent_publishers_allowed : undefined;
    const entryFeeRequired = typeof filters.entry_fee_required === 'boolean' ? filters.entry_fee_required : undefined;
    const sortBy = typeof filters.sort_by === 'string' ? filters.sort_by : undefined;
    const sortDirection = typeof filters.sort_direction === 'string' ? filters.sort_direction : 'asc';

    if (textSearch) summary.push(`Search: "${textSearch}"`);
    if (vetting && vetting.length) {
      const vettingLabels = vetting.map((v: string) => {
        if (v === 'green') return 'Approved';
        if (v === 'yellow') return 'Review';
        if (v === 'red') return 'Not suited';
        return v;
      });
      summary.push(`Vetting: ${vettingLabels.join(', ')}`);
    }
    if (qualityMin != null) summary.push(`Min Quality: ${qualityMin}%`);
    if (isbnRequired === true) summary.push('ISBN required');
    if (isbnRequired === false) summary.push('ISBN not required');
    if (indieAllowed === true) summary.push('Independent publishers allowed');
    if (indieAllowed === false) summary.push('Independent publishers not allowed');
    if (entryFeeRequired === true) summary.push('Paid entry fees');
    if (entryFeeRequired === false) summary.push('Free entry');
    if (sortBy) summary.push(`Sorted by: ${sortBy} ${sortDirection}`);

    if (summary.length === 0 && !jobMeta.selected_count) summary.push('All programs');
    return summary;
  };

  return (
    <div style={{ background: '#fff', color: '#111' }}>
      <style>{`
        @media print {
          @page {
            margin: 12mm;
            size: landscape;
            @bottom-right {
              content: "Page " counter(page) " of " counter(pages);
            }
          }
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .page-break { page-break-after: always; }
          .avoid-break { page-break-inside: avoid; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          tr { page-break-inside: avoid; }
          .page-number::after {
            content: counter(page);
          }
        }
        .page { padding: 12px; }
        .header { border-bottom: 2px solid #e5e7eb; padding-bottom: 12px; margin-bottom: 12px; }
        h1 { font-size: 16px; margin: 0 0 6px; font-weight: normal; }
        .meta-info { font-size: 11px; color: #6b7280; margin: 4px 0; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th, td { border: 1px solid #ddd; padding: 5px 6px; vertical-align: top; }
        thead th { background: #f3f4f6; text-align: left; font-weight: 600; }
        tbody tr:nth-child(14n) { page-break-after: auto; }
        a, a:visited { color: #000; text-decoration: underline; word-break: break-word; overflow-wrap: anywhere; }
        .footer { margin-top: 16px; padding-top: 8px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #6b7280; }
      `}</style>
      
      <div className="page">
        <div className="header avoid-break">
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', alignItems: 'center', gap: 16 }}>
            <div>
              <img src="/BAM-logo-embedded.svg" alt="BAM" style={{ height: 60, width: 'auto' }} />
            </div>
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 8 }}>
                <h1 style={{ margin: 0, fontSize: '16px', fontWeight: 'normal' }}>Search Report</h1>
                <button 
                  className="no-print" 
                  onClick={() => window.print()}
                  style={{ 
                    background: '#FDE047', 
                    color: '#111', 
                    border: '1px solid #111', 
                    borderRadius: 6, 
                    padding: '6px 12px', 
                    fontWeight: 700, 
                    cursor: 'pointer' 
                  }}
                >
                  Print
                </button>
              </div>
              {jobMeta && (
                <>
                  <div className="meta-info">
                    Generated: {new Date(jobMeta.created_at).toLocaleString('en-US', { 
                      weekday: 'short', 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric', 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                  <div className="meta-info">
                    {jobMeta.selected_count > 0 ? (
                      <>Selected programs: {jobMeta.selected_count}</>
                    ) : (
                      <>Filters: {getFilterSummary().join(' • ') || 'None'}</>
                    )}
                  </div>
                  <div className="meta-info">
                    Total results: {rows.length}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {loading && <div style={{ padding: 16 }}>Preparing print view…</div>}
        {error && !loading && (
          <div style={{ padding: 16, color: '#b91c1c' }}>
            {error === 'PRINT_JOB_NOT_FOUND_OR_EXPIRED' ? (
              <div>
                Print job not found or expired. Please close this tab and click "Print results" again.
              </div>
            ) : (
              <div>{error}</div>
            )}
          </div>
        )}
        
        {!loading && !error && (
          <>
            <table>
              <thead>
                <tr>
                  <th style={{ width: '3%' }}>#</th>
                  <th style={{ width: '4%' }}>BACI</th>
                  <th style={{ width: '16%' }}>Program Name</th>
                  <th style={{ width: '14%' }}>Organization</th>
                  <th style={{ width: '14%' }}>Website URL</th>
                  <th style={{ width: '14%' }}>Past Winners URL</th>
                  <th style={{ width: '4%', textAlign: 'center' }}>Cats</th>
                  <th style={{ width: '4%', textAlign: 'center' }}>Wins</th>
                  <th style={{ width: '7%', textAlign: 'center' }}>Vetting / QC</th>
                  <th style={{ width: '4%', textAlign: 'center' }}>Lock</th>
                  <th style={{ width: '16%' }}>My Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r: any, idx: number) => (
                  <tr key={r.id} className={idx > 0 && idx % 14 === 0 ? 'page-break' : ''}>
                    <td style={{ textAlign: 'center', color: '#6b7280', fontSize: '10px' }}>{idx + 1}</td>
                    <td style={{ fontWeight: 600 }}>{r.baci_program_code || '—'}</td>
                    <td>{r.name}</td>
                    <td style={{ fontSize: '10px' }}>{r.awarding_organization || '—'}</td>
                    <td style={{ fontSize: '10px' }}>
                      {r.website_url ? (
                        <a href={r.website_url} target="_blank" rel="noopener noreferrer">{r.website_url}</a>
                      ) : '—'}
                    </td>
                    <td style={{ fontSize: '10px' }}>
                      {r.past_winners_url ? (
                        <a href={r.past_winners_url} target="_blank" rel="noopener noreferrer">{r.past_winners_url}</a>
                      ) : '—'}
                    </td>
                    <td style={{ textAlign: 'center' }}>{typeof r.category_count === 'number' ? r.category_count : '—'}</td>
                    <td style={{ textAlign: 'center' }}>{typeof r.win_opportunities === 'number' ? r.win_opportunities : '—'}</td>
                    <td style={{ textAlign: 'center', fontSize: '10px' }}>
                      {(() => {
                        const status = r.vetting_status || '';
                        const qc = typeof r.data_quality_score === 'number' ? `${Math.round(r.data_quality_score)}%` : '—';
                        let vettingLabel = '—';
                        if (status === 'green') vettingLabel = 'OK';
                        else if (status === 'yellow') vettingLabel = 'REV';
                        else if (status === 'red') vettingLabel = 'NO';
                        return `${vettingLabel} / ${qc}`;
                      })()}
                    </td>
                    <td style={{ textAlign: 'center', fontSize: '10px' }}>
                      {r.data_locked ? 'Y' : 'N'}
                    </td>
                    <td style={{ fontSize: '10px', color: '#9ca3af', borderLeft: '2px solid #e5e7eb' }}>
                      {/* Empty comment field for manual notes */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            <div className="footer">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  Book Award Mastery — Search Report
                </div>
                <div className="no-print">
                  Job ID: {jobId}
                </div>
                <div>
                  Page <span className="page-number"></span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PrintResultsPage;
